// Button — variant(primary/secondary/ghost/danger), size(sm/md/lg), startIcon, endIcon, loading 지원.
// Radix 프리미티브 없이 순수 HTML button을 wrap (접근성은 네이티브 button 속성으로 충분).

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 버튼 좌측 아이콘 */
  startIcon?: ReactNode;
  /** 버튼 우측 아이콘 */
  endIcon?: ReactNode;
  /** 로딩 상태: startIcon을 스피너로 교체하고 disabled 처리 */
  loading?: boolean;
}

const variantClass: Record<ButtonVariant, string> = {
  primary:   'ui-btn--primary',
  secondary: 'ui-btn--secondary',
  ghost:     'ui-btn--ghost',
  danger:    'ui-btn--danger',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'ui-btn--sm',
  md: 'ui-btn--md',
  lg: 'ui-btn--lg',
};

/**
 * Locker UI — Button
 * @example
 * <Button variant="primary" size="md" startIcon={<Plus />} loading={false}>저장</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      startIcon,
      endIcon,
      loading = false,
      disabled,
      className = '',
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = [
      'ui-btn',
      variantClass[variant],
      sizeClass[size],
      loading ? 'ui-btn--loading' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...rest}
      >
        {/* 로딩 중이면 스피너, 아니면 startIcon */}
        {loading ? (
          <span className="ui-btn__spinner" aria-hidden="true" />
        ) : (
          startIcon && <span className="ui-btn__icon ui-btn__icon--start">{startIcon}</span>
        )}
        {children && <span className="ui-btn__label">{children}</span>}
        {endIcon && !loading && (
          <span className="ui-btn__icon ui-btn__icon--end">{endIcon}</span>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
